import { useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { getUser, isAuthed } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Compass, Gift, ArrowRight, Copy } from "lucide-react";

/**
 * GuestWelcome — one-shot celebratory landing shown right after a traveller
 * registers. Confirms the two headline perks (5% launch discount + Treasure
 * Map access) and forwards them to the room they were trying to book with
 * the promo code already carried in the returnUrl.
 */
export default function GuestWelcome() {
  const { lang } = useLang();
  const IT = lang === "it";
  const nav = useNavigate();
  const [params] = useSearchParams();
  const returnUrl = params.get("return") || "/guest/dashboard";
  const promoCode = params.get("promo") || "";
  const user = getUser();

  // Anti-abuse: if the user landed here without an active session, bounce
  // them back to the auth form. Guarantees the welcome screen only ever
  // renders as the last step of a successful registration.
  useEffect(() => {
    if (!isAuthed()) {
      nav(`/guest/auth?mode=register&return=${encodeURIComponent(returnUrl)}`, { replace: true });
    }
  }, [nav, returnUrl]);

  const copyCode = async () => {
    if (!promoCode) return;
    try {
      await navigator.clipboard.writeText(promoCode);
      toast.success(IT ? "Codice copiato" : "Code copied");
    } catch { /* silent */ }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{
        background: "radial-gradient(ellipse at top, #f7edd4 0%, #eddcb0 60%, #d9b982 100%)",
        color: "#3a2a17",
      }}
      data-testid="guest-welcome-page"
    >
      <div className="max-w-2xl w-full text-center relative">
        {/* Brand mark — the ONLY chrome on the page, per the branded-flow spec */}
        <div className="mb-6 inline-flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground grid place-items-center font-display italic text-lg">H</div>
          <span className="font-display italic text-xl" style={{ fontFamily: "'IM Fell English', Georgia, serif" }}>
            HospitalityHub
          </span>
        </div>

        <div className="uppercase tracking-[0.35em] text-[10px] text-primary font-semibold mb-3">
          {IT ? "Benvenut" + (user?.name ? "a/o" : "o") : "Welcome"}
        </div>
        <h1
          className="font-display italic mb-3 leading-tight"
          style={{ fontFamily: "'IM Fell English', Georgia, serif", fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}
        >
          {IT ? `Benvenuto ${user?.name || ""}, la tua Italia segreta ti aspetta.` : `Welcome ${user?.name || ""}, your secret Italy awaits.`}
        </h1>
        <p className="text-base italic mb-10 max-w-lg mx-auto" style={{ color: "#5b3d18" }}>
          {IT
            ? "Il tuo account è pronto. Ecco cosa hai già sbloccato — e cosa ti aspetta subito dietro il check-in."
            : "Your account is set. Here's what you've already unlocked — and what waits for you the moment you check in."}
        </p>

        <div className="grid gap-4 sm:grid-cols-2 mb-10 text-left">
          <div className="rounded-md border-2 border-primary/40 bg-white/60 backdrop-blur-sm p-5 shadow-sm" data-testid="welcome-perk-discount">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-5 h-5 text-primary"/>
              <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">
                {IT ? "Sconto di Lancio" : "Launch discount"}
              </div>
            </div>
            <div className="font-display italic text-4xl mb-1" style={{ fontFamily: "'IM Fell English', Georgia, serif" }}>
              -5%
            </div>
            <div className="text-sm italic mb-3" style={{ color: "#5b3d18" }}>
              {IT
                ? "Sulla tua prossima prenotazione. Applicato automaticamente."
                : "On your next booking. Applied automatically."}
            </div>
            {promoCode && (
              <button
                type="button"
                onClick={copyCode}
                className="w-full inline-flex items-center justify-between gap-2 border border-primary/50 rounded-full px-3 py-2 bg-primary/5 hover:bg-primary/10 transition-colors font-mono text-sm"
                data-testid="welcome-promo-code"
              >
                <span className="tracking-widest">{promoCode}</span>
                <Copy className="w-3.5 h-3.5"/>
              </button>
            )}
          </div>

          <div className="rounded-md border-2 border-primary/40 bg-white/60 backdrop-blur-sm p-5 shadow-sm" data-testid="welcome-perk-map">
            <div className="flex items-center gap-2 mb-2">
              <Compass className="w-5 h-5 text-primary"/>
              <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">
                {IT ? "Mappa del Tesoro" : "Treasure Map"}
              </div>
            </div>
            <div className="font-display italic text-2xl mb-1 leading-tight" style={{ fontFamily: "'IM Fell English', Georgia, serif" }}>
              {IT ? "Storie, tavole d'autore e un premio finale." : "Stories, chef's tables and a final prize."}
            </div>
            <div className="text-sm italic" style={{ color: "#5b3d18" }}>
              {IT
                ? "Ogni città ha la sua mappa. Si sblocca automaticamente al check-in."
                : "Every city has its own map. Unlocks at check-in."}
            </div>
          </div>
        </div>

        <Button
          onClick={() => nav(returnUrl)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base rounded-full gap-2 lift"
          data-testid="welcome-continue"
        >
          <Sparkles className="w-4 h-4"/>
          {IT ? "Torna alla tua camera" : "Back to your room"}
          <ArrowRight className="w-4 h-4 ml-1"/>
        </Button>

        <div className="text-xs italic mt-6" style={{ color: "#5b3d18" }}>
          {IT ? "Oppure " : "Or "}
          <Link to="/guest/dashboard" className="underline" data-testid="welcome-dashboard-link">
            {IT ? "vai al tuo diario di viaggio" : "go to your traveller diary"}
          </Link>
        </div>
      </div>
    </div>
  );
}
